/**
 * @module
 * Contains functions for optimized fetching of replaceable lists associated with a pubkey.
 */ import DataLoader from "./dataloader.js";
import { normalizeURL } from "@jsr/nostr__tools/utils";
import { pool, purgatory, replaceableStore } from "./global.js";
import { METADATA_QUERY_RELAYS, RELAYLIST_RELAYS } from "./defaults.js";
import { identity, isHex32 } from "./utils.js";
let serial = 0;
export const isFresh = Symbol('event was just downloaded or force-updated, not loaded from cache');
/**
 * A ListFetcher for kind:3 follow lists.
 */ export const loadFollowsList = makeListFetcher(3, METADATA_QUERY_RELAYS, itemsFromTags((tag)=>{
  if (tag.length >= 2 && tag[0] === 'p' && isHex32(tag[1])) {
    return tag[1];
  }
}));
/**
 * A ListFetcher for kind:10101 "good wiki authors" list
 */ export const loadWikiAuthors = makeListFetcher(10101, [], itemsFromTags((tag)=>{
  if (tag.length >= 2 && tag[0] === 'p' && isHex32(tag[1])) {
    return tag[1];
  }
}));
/**
 * A ListFetcher for kind:10102 "good wiki relays" list
 */ export const loadWikiRelays = makeListFetcher(10102, [], itemsFromTags((tag)=>{
  if (tag.length >= 2 && tag[0] === 'relay') {
    return tag[1];
  }
}));
/**
 * A ListFetcher for kind:10012 "favorite relays" list
 */ export const loadFavoriteRelays = makeListFetcher(10012, [], itemsFromTags((tag)=>{
  if (tag.length >= 2) {
    switch(tag[0]){
      case 'relay':
        return normalizeURL(tag[1]);
      case 'a':
        const spl = tag[1].split(':');
        if (!isHex32(spl[1]) || spl[0] !== '30002') return undefined;
        return {
          identifier: spl.slice(2).join(':'),
          pubkey: spl[1],
          kind: parseInt(spl[0]),
          relays: tag[2] ? [
            tag[2]
          ] : []
        };
    }
  }
}));
/**
 * A ListFetcher for kind:10002 relay lists.
 */ export const loadRelayList = makeListFetcher(10002, RELAYLIST_RELAYS, itemsFromTags((tag)=>{
  if (tag[0] !== 'r') return undefined;
  if (tag.length === 2) {
    return {
      url: normalizeURL(tag[1]),
      read: true,
      write: true
    };
  } else if (tag[2] === 'read') {
    return {
      url: normalizeURL(tag[1]),
      read: true,
      write: false
    };
  } else if (tag[2] === 'write') {
    return {
      url: normalizeURL(tag[1]),
      read: false,
      write: true
    };
  }
}));
/**
 * A ListFetcher for kind:10000 mute lists.
 */ export const loadMuteList = makeListFetcher(10000, [], itemsFromTags((tag)=>{
  if (tag.length >= 2) {
    switch(tag[0]){
      case 'p':
        if (isHex32(tag[1])) {
          return {
            label: 'pubkey',
            value: tag[1]
          };
        }
        break;
      case 'e':
        if (isHex32(tag[1])) {
          return {
            label: 'thread',
            value: tag[1]
          };
        }
        break;
      case 't':
        return {
          label: 'hashtag',
          value: tag[1]
        };
      case 'word':
        return {
          label: 'word',
          value: tag[1]
        };
    }
    return undefined;
  }
}));
export const loadBookmarks = makeListFetcher(10003, [], itemsFromTags((tag)=>{
  if (tag.length >= 2 && (tag[0] === 'e' || tag[0] === 'a') && tag[1]) {
    return tag[1];
  }
}));
export const loadBlossomServers = makeListFetcher(10063, [], (event)=>event ? event.tags.filter(([k, v])=>k === 'server' && v).map(([, url])=>'http' + normalizeURL(url).substring(2 /* 'ws' */ )).filter(Boolean) : []);
export const loadEmojis = makeListFetcher(10030, [], itemsFromTags((tag)=>{
  if (tag.length < 2) return;
  if (tag[0] === 'a') {
    const spl = tag[1].split(':');
    if (!isHex32(spl[1]) || spl[0] !== '30030') return undefined;
    return {
      identifier: spl.slice(2).join(':'),
      pubkey: spl[1],
      kind: parseInt(spl[0]),
      relays: tag[2] ? [
        tag[2]
      ] : []
    };
  }
  if (tag.length < 3 || tag[0] !== 'emoji') return undefined;
  return {
    shortcode: tag[1],
    url: tag[2]
  };
}));
export const loadPins = makeListFetcher(10001, [], itemsFromTags((tag)=>{
  if (tag.length >= 2 && tag[0] === 'e' && tag[1]) {
    return tag[1];
  }
}));
export function itemsFromTags(tagProcessor) {
  return (event)=>{
    const items = event ? event.tags.map(tagProcessor).filter(identity) : [];
    return items;
  };
}
/**
 * makeListFetcher is the function used to create a ListFetcher which should then be used throughout the rest of
 * an application's lifetime.
 *
 * It is generally suited to abstract loading and fetching of NIP-51 lists.
 *
 * Take a look at the loadFollowsList and and loadRelayList implementations for more insight on how to use this.
 */ export function makeListFetcher(kind, hardcodedRelays, process) {
  const dataloader = new DataLoader((requests)=>new Promise(async (resolve)=>{
      const toFetch = {};
      const pendingSaves = [];
      const now = Math.round(Date.now() / 1000);
      // try to get from store first -- also set up the results array with defaults
      // use 3-tuple with empty dtag to get single event return type
      const stored = await replaceableStore.loadReplaceables(requests.map((r)=>[
          kind,
          r.target,
          ''
        ]));
      let results = stored.map(([lastAttempt, storedEvent], i)=>{
        const req = requests[i];
        req.index = i;
        if (typeof req.refreshStyle === 'object') {
          // we have the event right here, so just use it
          const final = {
            event: req.refreshStyle,
            items: process(req.refreshStyle),
            [isFresh]: true
          };
          pendingSaves.push(replaceableStore.saveEvent(req.refreshStyle, {
            lastAttempt: now
          }));
          return final;
        } else if (!storedEvent) {
          // we don't have anything for this key, fill in with a placeholder
          if (req.refreshStyle !== false) {
            return new Promise((resolve)=>{
              toFetch[req.target] = {
                req,
                resolve,
                resolved: false
              };
            });
          } else {
            return {
              items: req.defaultItems || [],
              event: null,
              [isFresh]: false
            };
          }
        } else if (req.refreshStyle === true || !lastAttempt || lastAttempt < now - 60 * 60 * 24 * 2) {
          // we have something but it's old (2 days), so we will use it but still try to fetch a new version
          const good = {
            event: storedEvent,
            items: process(storedEvent),
            [isFresh]: false
          };
          if (req.refreshStyle !== false) {
            return new Promise((resolve)=>{
              resolve(good);
              toFetch[req.target] = {
                req,
                resolve,
                resolved: true
              };
            });
          } else {
            return good;
          }
        } else {
          // this one is so good we won't try to fetch it again
          return {
            event: storedEvent,
            items: process(storedEvent),
            [isFresh]: false
          };
        }
      });
      // wait for pendingSaves (from when an event is passed through refreshStyle)
      if (pendingSaves.length > 0) await Promise.all(pendingSaves);
      // resolve immediately (pending reqs will return a promise thus resolve later)
      resolve(results);
      if (Object.keys(toFetch).length === 0) {
        return;
      }
      const filterByRelay = {};
      for (const { req: { target, relays: reqRelays } } of Object.values(toFetch)){
        const relays = reqRelays.slice(0, Math.min(4, reqRelays.length));
        do {
          relays.push(randomPick(hardcodedRelays));
        }while (relays.length < 3)
        for(let j = 0; j < relays.length; j++){
          const url = relays[j];
          let filter = filterByRelay[url];
          if (!filter) {
            filter = {
              kinds: [
                kind
              ],
              authors: []
            };
            filterByRelay[url] = filter;
          }
          filter.authors?.push(target);
        }
      }
      try {
        let handle;
        // eslint-disable-next-line prefer-const
        handle = pool.subscribeMap(Object.entries(filterByRelay).map(([url, filter])=>({
            url,
            filter
          })), {
          label: `kind:${kind}:batch(${Object.keys(toFetch).length})`,
          onevent (evt) {
            const { req, resolve, resolved } = toFetch[evt.pubkey];
            if (resolved) return;
            resolve({
              event: evt,
              items: process(evt),
              [isFresh]: true
            });
            toFetch[evt.pubkey].resolved = true;
            replaceableStore.saveEvent(evt, {
              lastAttempt: now
            });
          },
          oneose () {
            handle?.close();
          },
          async onclose () {
            // resolve promises for targets that didn't receive any events with their placeholder data
            for (const { req, resolve, resolved } of Object.values(toFetch)){
              if (resolved) continue;
              const placeholder = {
                items: req.defaultItems || [],
                event: null,
                [isFresh]: false
              };
              resolve(placeholder);
              // save blank events for targets that didn't receive any events (they won't be really saved)
              replaceableStore.saveEvent({
                id: '0'.repeat(64),
                pubkey: req.target,
                kind: kind,
                sig: '0'.repeat(128),
                tags: [],
                created_at: 0,
                content: ''
              }, {
                lastAttempt: now
              });
            }
          }
        });
      } catch (err) {
        resolve(results.map((_)=>err));
      }
    }), {
    cacheKeyFn: (req)=>req.target,
    transformCacheHit (v) {
      v[isFresh] = false;
      return v;
    }
  });
  return async function(pubkey, hints = [], refreshStyle, defaultItems) {
    if (refreshStyle === null) {
      // refreshStyle === null: reset cache and return empty
      await replaceableStore.deleteReplaceable(kind, pubkey);
      dataloader._cacheMap.delete(pubkey);
      return {
        items: defaultItems || [],
        event: null,
        [isFresh]: true
      };
    }
    let relays = hints;
    if (kind === 10002) {
      return await dataloader.load({
        target: pubkey,
        relays,
        refreshStyle,
        defaultItems
      });
    } else {
      const rl = await loadRelayList(pubkey, hints, refreshStyle);
      relays.push(...rl.items.filter(({ write, url })=>write && purgatory.allowConnectingToRelay(url, [
          'read',
          [
            {
              kinds: [
                kind
              ]
            }
          ]
        ])).map(({ url })=>url).slice(0, 3));
      const req = {
        target: pubkey,
        relays,
        refreshStyle,
        defaultItems
      };
      if (refreshStyle) {
        dataloader.clear(req);
      }
      return await dataloader.load(req);
    }
  };
}
function randomPick(list) {
  return list[serial++ % list.length];
}
//# sourceMappingURL=lists.js.map